import { ZoomOutMap, LocationOn } from '@mui/icons-material';
import { Button, SearchBar } from '@practium/ui';
import styles from './EditRegionLeftPanel.module.css';
import { STATE_NAMES } from '../../constants/stateNames';
import { StateMiniMapSVG } from '../Map/BrazilMap/StateMiniMapSVG';
import { useEditRegionPanel } from '../../hooks/useEditRegionPanel';

interface EditRegionLeftPanelProps {
	selectedStateCodes: string[];
	searchQuery: string;
	onSearchChange: (q: string) => void;
	onToggleState: (code: string) => void;
}

export const EditRegionLeftPanel = ({
	selectedStateCodes,
	searchQuery,
	onSearchChange,
	onToggleState,
}: EditRegionLeftPanelProps) => {
	const {
		expandedStateCode,
		filteredStates,
		filteredCities,
		loading,
		allFilteredSelected,
		someFilteredSelected,
		handleExpand,
		toggleCity,
		isCitySelected,
		toggleAllCities,
		handleBack,
	} = useEditRegionPanel({ searchQuery, onSearchChange });

	if (expandedStateCode) {
		const stateName = STATE_NAMES[expandedStateCode] ?? expandedStateCode;
		return (
			<div className={styles.panel}>
				<div className={styles.cityViewHeader}>
					<button className={styles.statesBtn}>
						<LocationOn sx={{ fontSize: '1.125rem' }} />
						<span>{stateName}</span>
					</button>
					<Button variant="secondary" className={styles.backBtn} onClick={handleBack}>
						Voltar
					</Button>
				</div>
				<SearchBar value={searchQuery} onChange={onSearchChange} />
				<div className={styles.cityList}>
					{loading ? (
						<span className={styles.cityLoading}>Carregando cidades...</span>
					) : (
						<>
							{filteredCities.length > 0 && (
								<label
									className={`${styles.cityItem} ${styles.cityItemAll}`}
									onClick={toggleAllCities}
								>
									<input
										type="checkbox"
										className={styles.cityCheckbox}
										checked={allFilteredSelected}
										ref={(el) => {
											if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
										}}
										onChange={toggleAllCities}
									/>
									<span className={styles.cityName}>Selecionar todas</span>
								</label>
							)}
							{filteredCities.map((city) => {
								const selected = isCitySelected(city.id);
								return (
									<label key={city.id} className={styles.cityItem}>
										<input
											type="checkbox"
											className={styles.cityCheckbox}
											checked={selected}
											onChange={() => toggleCity(city.id)}
										/>
										<span className={styles.cityName}>{city.name}</span>
										<span className={styles.cityMesoregion}>{city.mesoregion}</span>
									</label>
								);
							})}
						</>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.panel}>
			<button className={styles.statesBtn}>
				<LocationOn sx={{ fontSize: '1.125rem' }} />
				<span>Estados</span>
			</button>

			<SearchBar value={searchQuery} onChange={onSearchChange} />

			<div className={styles.statesGrid}>
				{filteredStates.map((code) => {
					const isSelected = selectedStateCodes.includes(code);
					return (
						<div
							key={code}
							className={styles.stateCard}
							onClick={() => onToggleState(code)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === 'Enter' && onToggleState(code)}
							aria-pressed={isSelected}
						>
							<div
								aria-pressed={isSelected}
								className={`${styles.stateContainer} ${isSelected ? styles.stateContainerSelected : ''}`}
							>
								<button
									className={styles.expandBtn}
									onClick={(e) => handleExpand(e, code)}
									aria-label="Expandir"
								>
									<ZoomOutMap sx={{ fontSize: '1.5rem' }} />
								</button>
								<div className={styles.stateMapWrapper}>
									<StateMiniMapSVG stateCode={code} isSelected={isSelected} />
								</div>
							</div>
							<span className={styles.stateLabel}>{STATE_NAMES[code] ?? code}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};
